/* ========================================================================= *
 * AGENT STAMP - which AI landed this. Brian Kass, 2026-10-26:
 *   "show if it's Dot (ChatGPT) or Jarvis (Claude) that completes a task or
 *    something gets automated and lands its stamped."
 *
 * The backend stamps `agent` server-side from the authenticated identity
 * (sr-portal-backend agent-attribution.js). This file only PRESENTS it. It
 * never infers an agent from an actor name, a note or a log prefix: a value
 * it does not recognise renders as Unstamped, which is the truth about it.
 * ========================================================================= */

export const AGENT_META = Object.freeze({
  jarvis: { key: 'jarvis', name: 'Jarvis', maker: 'Claude', tone: 'jarvis' },
  dot: { key: 'dot', name: 'Dot', maker: 'ChatGPT', tone: 'dot' },
  human: { key: 'human', name: 'Human', maker: '', tone: 'human' },
  unstamped: { key: 'unstamped', name: 'Unstamped', maker: '', tone: 'unstamped' },
})

export function normalizeAgent(v) {
  const k = String(v ?? '').trim().toLowerCase()
  return Object.prototype.hasOwnProperty.call(AGENT_META, k) ? k : 'unstamped'
}

export function agentMeta(v) {
  return AGENT_META[normalizeAgent(v)]
}

/** "Jarvis · Claude", "Dot · ChatGPT", "Human", "Unstamped". */
export function agentLabel(v) {
  const m = agentMeta(v)
  return m.maker ? `${m.name} · ${m.maker}` : m.name
}

/** Plain-words reading of a time, "4m ago", "3h ago", "2d ago", or a date. */
export function ago(at, now = Date.now()) {
  const t = Date.parse(at || '')
  if (!Number.isFinite(t)) return ''
  const s = Math.max(0, Math.round((now - t) / 1000))
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  if (s < 86400) return `${Math.round(s / 3600)}h ago`
  if (s < 86400 * 14) return `${Math.round(s / 86400)}d ago`
  return new Date(t).toISOString().slice(0, 10)
}

/* What a ledger field means in a rep's words. Unknown fields pass through. */
const FIELD_WORDS = [
  [/^resolve:crowned$/, 'crowned'], [/^resolve:manual$/, 'crowned by hand'],
  [/^resolve:split$/, 'split into titles'], [/^resolve:not_yet$/, 'moved date'],
  [/^resolve:not_found$/, 'searched, not found'], [/^resolve:recheck$/, 'rechecked'],
  [/^resolve:enrich$/, 'enriched crown'], [/^resolve:/, 'resolved'],
  [/^champion_request$/, 'champion requested'], [/^sr_assigned_rep$/, 'assignment changed'],
  [/^season_rollover$/, 'season rolled over'], [/archive/, 'archived'],
  [/^sr_title_tier$/, 'tier tagged'], [/trade/, 'trade'],
]
export function landingWords(field) {
  const f = String(field || '')
  for (const [re, words] of FIELD_WORDS) if (re.test(f)) return words
  return f ? f.replace(/_/g, ' ') : 'change'
}

/** Accessible one-liner for a badge, e.g. "Landed by Jarvis · Claude as svc:x, 3h ago". */
export function badgeTitle({ agent, actor, at } = {}, now = Date.now()) {
  const parts = [`Landed by ${agentLabel(agent)}`]
  if (actor) parts.push(`as ${actor}`)
  const when = ago(at, now)
  return when ? `${parts.join(' ')}, ${when}` : parts.join(' ')
}

/** Count landings per agent; every key present, unknown values count as unstamped. */
export function tally(landings = []) {
  const out = { jarvis: 0, dot: 0, human: 0, unstamped: 0 }
  for (const l of landings || []) out[normalizeAgent(l?.agent)]++
  return out
}

/* The record ids to ask about for a body's recent activity: the most recently
 * dated records first, capped at the route's limit (25). Dateless records go
 * last rather than being dropped, so a body with no dates still answers. */
export function recentCandidateIds(rows = [], n = 25) {
  const when = (c) => Date.parse(c?.championDetectedAt || c?.detectedAt || c?.updatedAt || c?.effDate || '') || 0
  return [...(rows || [])].filter((c) => c && c.id != null)
    .sort((a, b) => when(b) - when(a))
    .slice(0, n).map((c) => String(c.id))
}
